// ─── DateRangeFilter.jsx ─────────────────────────────────────────────────────
// Preset + custom date range filter. Drop above any data table.
// Import as: import DateRangeFilter from "./DateRangeFilter";
//
// Usage:
//   const [dateRange, setDateRange] = useState({ from: "", to: "" });
//
//   // Filter your data:
//   const filtered = data.filter(row => {
//     const d = new Date(row.created_at);
//     if (dateRange.from && d < new Date(dateRange.from)) return false;
//     if (dateRange.to   && d > new Date(dateRange.to + "T23:59:59")) return false;
//     return true;
//   });
//
//   // In JSX:
//   <DateRangeFilter from={dateRange.from} to={dateRange.to} onChange={setDateRange} />

import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";

export default function DateRangeFilter({ from, to, onChange }) {
  const C = useTheme();
  const { t } = useTranslation();

  const PRESETS = [
    { labelKey: "dateFilter.today", days: 0 },
    { labelKey: "dateFilter.days7",  days: 7 },
    { labelKey: "dateFilter.days30", days: 30 },
    { labelKey: "dateFilter.days90", days: 90 },
    { labelKey: "dateFilter.year1",  days: 365 },
  ];
  function applyPreset(days) {
    const end   = new Date();
    const start = new Date();
    if (days === 0) {
      // Today only
      onChange({
        from: end.toISOString().slice(0, 10),
        to:   end.toISOString().slice(0, 10),
      });
    } else {
      start.setDate(start.getDate() - days);
      onChange({
        from: start.toISOString().slice(0, 10),
        to:   end.toISOString().slice(0, 10),
      });
    }
  }

  const hasFilter = from || to;

  const inputStyle = {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6,
    color: C.text, fontSize: 12, padding: "5px 8px",
    fontFamily: "'DM Mono', monospace", outline: "none",
    colorScheme: "dark",
  };

  const presetStyle = (active) => ({
    background: active ? C.accentGlow : "transparent",
    border: `1px solid ${active ? C.accent : C.border}`,
    borderRadius: 5, color: active ? C.accent : C.textMid,
    fontSize: 11, padding: "4px 9px", cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
  });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ color: C.textDim, fontSize: 11, marginRight: 2 }}>{t('common.date')}:</span>
      {PRESETS.map(p => (
        <button key={p.labelKey} onClick={() => applyPreset(p.days)}
          style={presetStyle(false)}>
          {t(p.labelKey)}
        </button>
      ))}
      <input
        type="date"
        value={from || ""}
        onChange={e => onChange({ from: e.target.value, to })}
        style={inputStyle}
      />
      <span style={{ color: C.textDim, fontSize: 11 }}>→</span>
      <input
        type="date"
        value={to || ""}
        onChange={e => onChange({ from, to: e.target.value })}
        style={inputStyle}
      />
      {hasFilter && (
        <button
          onClick={() => onChange({ from: "", to: "" })}
          style={{ background: "transparent", border: "none", color: C.textDim,
            fontSize: 11, cursor: "pointer", padding: "4px 6px" }}
        >
          ✕ {t('common.clearFilters')}
        </button>
      )}
    </div>
  );
}